import * as React from 'react';
import { useDispatch } from 'react-redux';
import { useLocation, useNavigate } from 'react-router-dom';

import { PanelWrapper } from '@app/components/Filters/FilterComponents/PanelWrapper';
import { useSelector } from '@app/hooks/useSelector';

import { PackagesFilterOption, PackagesFilterValue } from '@app/objects/CruisePackages';
import { PackagesFilterKeys, PackagesFormValues, isPackagesKey } from '@app/objects/Filters/Packages';
import { List, SearchResult } from '@app/objects/List';
import { Nullable, isArray } from '@app/objects/Utility';
import { SearchPackagesPageContent } from '@app/pages/SearchPage/SearchPackagesPageContent';
import { routesToListItems } from '@app/services/adaptors/SearchListAdaptor';
import { request, RequestConfig } from '@app/services/api';
import { getActionCreators } from '@app/store/MainPage/ListActions';
import { useDebounce } from '@app/hooks/useDebounce';

import { SelectableListItemProps } from '@app/components/SelectableList/SelectableList';
import { useSearchPackagesURLManager } from '@app/hooks/useSearchPackagesURLManager';
import { FilterURLTraverser } from '@app/services/searchPackages/FilterURLTraverser';
import { ListURLTraverser } from '@app/services/searchPackages/ListURLTraverser';
import { getOption, getOrder } from '@app/pages/SearchPage/SorterOptions';
import { DispatchType, RootStore } from '@app/store';

import { Icon } from '@image/icon';

function normalizeOptionName(name: string): string {
	return name
		.normalize('NFKD')
		.replace(/[\u0300-\u036f]/g, '')
		.replace(/[\u200B-\u200D\uFEFF\u00AD]/g, '')
		.replace(/[\u2010-\u2015]/g, '-')
		.replace(/\s+/g, ' ')
		.trim()
		.toLowerCase();
}

function dedupeListItems<TValue extends PackagesFilterValue>(
	list: Array<SelectableListItemProps<TValue>>,
): Array<SelectableListItemProps<TValue>> {
	const keys = new Set<string>();

	return list.filter((item: SelectableListItemProps<TValue>) => {
		const key = normalizeOptionName(item.label.replace(/\s*\(\d+\)\s*$/, ''));
		if (keys.has(key)) return false;

		keys.add(key);

		return true;
	});
}

function buildUniqueChildrenByParent<TValue extends PackagesFilterValue>(
	children: Array<PackagesFilterOption<TValue, TValue>>,
): Map<TValue, Array<PackagesFilterOption<TValue, TValue>>> {
	return children
		.reduce((map: Map<TValue, Array<PackagesFilterOption<TValue, TValue>>>, child: PackagesFilterOption<TValue, TValue>) => {
			const { parentId } = child;
			if (parentId === undefined) return map;

			const siblings = map.get(parentId) ?? [];
			const normalizedChildName = normalizeOptionName(child.name);
			if (!siblings.some((item: PackagesFilterOption<TValue, TValue>) => (
				item.id === child.id
				|| normalizeOptionName(item.name) === normalizedChildName
			))) {
				siblings.push(child);
			}

			map.set(parentId, siblings);

			return map;
		}, new Map<TValue, Array<PackagesFilterOption<TValue, TValue>>>());
}

function mapChildrenToListItems<TValue extends PackagesFilterValue>(
	item: SelectableListItemProps<TValue>,
	childrenByParent: Map<TValue, Array<PackagesFilterOption<TValue, TValue>>>,
): SelectableListItemProps<TValue> {
	const normalizedParentName = normalizeOptionName(item.label.replace(/\s*\(\d+\)\s*$/, ''));
	const mappedChildren = childrenByParent.get(item.value) ?? [];
	if (!mappedChildren.length) return item;

	const sameNamedChildren = mappedChildren.filter((child: PackagesFilterOption<TValue, TValue>) => (
		normalizeOptionName(child.name) === normalizedParentName
	));
	const uniqueOtherChildren = mappedChildren.filter((child: PackagesFilterOption<TValue, TValue>) => (
		normalizeOptionName(child.name) !== normalizedParentName
	));

	if (!uniqueOtherChildren.length && sameNamedChildren.length) {
		const [firstChild] = sameNamedChildren;

		return {
			value: firstChild.id,
			label: `${firstChild.name} (${firstChild.count})`,
		};
	}

	return {
		...item,
		list: dedupeListItems((uniqueOtherChildren.length ? uniqueOtherChildren : mappedChildren)
			.map((child: PackagesFilterOption<TValue, TValue>) => ({
				value: child.id,
				label: `${child.name} (${child.count})`,
			}))),
	};
}

function dedupeTreeItems<TValue extends PackagesFilterValue>(
	list: Array<SelectableListItemProps<TValue>>,
): Array<SelectableListItemProps<TValue>> {
	const seenLabels = new Set<string>();

	return list.reduce((acc: Array<SelectableListItemProps<TValue>>, item: SelectableListItemProps<TValue>) => {
		const normalizedItemLabel = normalizeOptionName(item.label.replace(/\s*\(\d+\)\s*$/, ''));
		if (seenLabels.has(normalizedItemLabel)) return acc;

		seenLabels.add(normalizedItemLabel);

		if (!item.list?.length) {
			acc.push(item);

			return acc;
		}

		const uniqueChildren = item.list.filter((child: SelectableListItemProps<TValue>) => {
			const normalizedChildLabel = normalizeOptionName(child.label.replace(/\s*\(\d+\)\s*$/, ''));
			if (seenLabels.has(normalizedChildLabel)) return false;

			seenLabels.add(normalizedChildLabel);

			return true;
		});

		acc.push({
			...item,
			list: uniqueChildren,
		});

		return acc;
	}, []);
}

function getOptions<TValue extends PackagesFilterValue>(
	list?: Array<PackagesFilterOption<TValue>>,
	children?: Array<PackagesFilterOption<TValue, TValue>>,
): Array<SelectableListItemProps<TValue>> {
	if (!list) return [];

	const uniqList: Array<PackagesFilterOption<TValue>> = list.reduce(
		(items: Array<PackagesFilterOption<TValue>>, item: PackagesFilterOption<TValue>) => {
			const normalizedItemName = normalizeOptionName(item.name);
			const hasDuplicate = items.some((current: PackagesFilterOption<TValue>) => (
				current.id === item.id
				|| normalizeOptionName(current.name) === normalizedItemName
			));

			if (!hasDuplicate) items.push(item);

			return items;
		},
		[],
	);

	const res: Array<SelectableListItemProps<TValue>> = dedupeListItems(uniqList
		.map((item: PackagesFilterOption<TValue>) => ({
			value: item.id,
			label: `${item.name} (${item.count})`,
		})));

	if (children) {
		const topLevelIdToName = new Map<string, string>(
			uniqList.map((item: PackagesFilterOption<TValue>) => [String(item.id), normalizeOptionName(item.name)]),
		);
		const topLevelNames = new Set<string>(topLevelIdToName.values());
		const sameNamedChildByParent = new Map<string, PackagesFilterOption<TValue, TValue>>();

		children.forEach((child: PackagesFilterOption<TValue, TValue>) => {
			if (child.parentId === undefined) return;

			const normalizedChildName = normalizeOptionName(child.name);
			const parentName = topLevelIdToName.get(String(child.parentId));

			if (parentName === normalizedChildName) {
				sameNamedChildByParent.set(String(child.parentId), child);
			}
		});

		const topLevelWithChildValues = res.map((item: SelectableListItemProps<TValue>) => {
			const sameNamedChild = sameNamedChildByParent.get(String(item.value));
			if (!sameNamedChild) return item;

			return {
				value: sameNamedChild.id,
				label: `${sameNamedChild.name} (${sameNamedChild.count})`,
			};
		});

		const nonDuplicateChildren = children.filter((child: PackagesFilterOption<TValue, TValue>) => {
			const normalizedChildName = normalizeOptionName(child.name);

			return !topLevelNames.has(normalizedChildName);
		});

		const uniqChildrenByParent = buildUniqueChildrenByParent(nonDuplicateChildren);
		const mappedItems = topLevelWithChildValues
			.map((item: SelectableListItemProps<TValue>) => mapChildrenToListItems(item, uniqChildrenByParent));

		return dedupeTreeItems(mappedItems);
	}

	return res;
}

const initialPage: number = 1;
const pageSize: number = 10;

const normalizePage = (value: number | undefined, fallback: number = initialPage): number => {
	const safeValue = Number.isFinite(value) ? Math.trunc(Number(value)) : fallback;

	return Math.max(1, safeValue);
};

export const SearchPackagesPage = () => {
	const location = useLocation();
	const navigate = useNavigate();
	const store = useSelector((state: RootStore) => state.packagesFilters);

	const manager = useSearchPackagesURLManager();

	const [loading, setLoading] = React.useState<boolean>(false);
	const [list, setList] = React.useState<Nullable<List>>(null);
	const [filters, setFilters] = React.useState<PackagesFormValues>(() => manager.getAll());

	const dispatch = useDispatch<DispatchType<'packagesFilters'>>();
	function fetchData() {
		const filtersQuery = manager.toString(new FilterURLTraverser());
		const listQuery = manager.toString(new ListURLTraverser(pageSize));

		const filtersUrl = `api/search/v1/packages/filters?${filtersQuery}`;
		const listUrl = `api/search/v1/packages?${listQuery}`;
		const config: RequestConfig = {
			tag: 'package-request',
		};

		setLoading(true);

		// Fetch filters
		const factory = getActionCreators(filtersUrl, 'packagesFilters');
		dispatch(factory.request());

		request(listUrl, config)
			.then((res: SearchResult) => {
				const currentPage = normalizePage(Math.floor(res.offset / pageSize) + 1);
				console.log('[OVC pagination applied]', {
					offset: res.offset,
					pageSize,
					currentPage,
					total: res.routesTotal,
				});
				setList({
					total: {
						items: res.routesTotal,
						subItems: res.voyagesTotal,
					},
					list: routesToListItems(res.routes),
					offset: res.offset,
					pageSize,
					pageStart: currentPage,
				});
			})
			.catch((err: Error) => {
				if (err.message !== 'The user aborted a request.') {
					console.warn('Get cruises list failed: ', err.message);
				}
			})
			.finally(() => setLoading(false));
	}
	const debounce = useDebounce<PackagesFormValues>(fetchData, 750);

	React.useEffect(() => {
		const url = `?${manager.toString()}`;
		const filters = manager.getAll();
		setFilters(filters);

		if (url !== location.search) {
			navigate(url);
		}
	}, [location.search]);

	React.useEffect(() => {
		debounce(filters);
	}, [filters]);

	const handleReset = () => {
		manager.reset();
		const url = manager.toString();

		navigate(`?${url}`);
	};

	const handleSearch = (values: Partial<PackagesFormValues>) => {
		manager.setAll(values);
		const url = manager.toString();

		if (location.search !== url) {
			navigate(`?${url}`);
		}
	};

	const hasSelection = Object.keys(filters)
		.filter(isPackagesKey)
		.filter((key: keyof PackagesFormValues) => isArray(filters[key]))
		.filter((key: keyof PackagesFormValues) => (filters[key] as Array<never>).length).length > 0;

	return (
		<SearchPackagesPageContent
			loading={loading}
			list={list}
			sorter={getOption(filters[PackagesFilterKeys.TAG], filters[PackagesFilterKeys.DIRECTION]).value}
			onChangeSorter={(value: number) => {
				const order = getOrder(value);
				if (!order) return;

				handleSearch({
					[PackagesFilterKeys.PAGE]: initialPage,
					[PackagesFilterKeys.TAG]: order.tag,
					[PackagesFilterKeys.DIRECTION]: order.direction,
				});
			}}
			currentPage={normalizePage(filters[PackagesFilterKeys.PAGE])}
			onChangePage={(page: number) => {
				handleSearch({
					[PackagesFilterKeys.PAGE]: normalizePage(page),
				});
			}}
			onReset={handleReset}
			canReset={hasSelection}
		>
			<PanelWrapper
				title="Type"
				Icon={Icon.Tag}
				list={getOptions(store.item?.[PackagesFilterKeys.TYPE])}
				loading={store.loading}

				value={filters[PackagesFilterKeys.TYPE]}
				onChange={(values: Array<number>) => {
					if (!isArray(values)) return;

					handleSearch({
						[PackagesFilterKeys.TYPE]: values,
						[PackagesFilterKeys.PAGE]: initialPage,
					});
				}}
			/>
			<PanelWrapper
				title="Destination"
				Icon={Icon.Location}
				list={getOptions(store.item?.[PackagesFilterKeys.DESTINATIONREGION], store.item?.[PackagesFilterKeys.DESTINATION])}
				loading={store.loading}

				value={filters[PackagesFilterKeys.DESTINATION]}
				onChange={(values: Array<string>) => {
					handleSearch({
						[PackagesFilterKeys.DESTINATION]: values,
						[PackagesFilterKeys.PAGE]: initialPage,
					});
				}}
			/>
			<PanelWrapper
				title="Travel Dates"
				Icon={Icon.Calendar}
				list={getOptions(store.item?.[PackagesFilterKeys.DATE])}
				loading={store.loading}

				value={filters[PackagesFilterKeys.DATE]}
				onChange={(values: Array<number>) => {
					if (!isArray(values)) return;

					handleSearch({
						[PackagesFilterKeys.DATE]: values,
						[PackagesFilterKeys.PAGE]: initialPage,
					});
				}}
			/>
			<PanelWrapper
				title="Vacation Duration"
				Icon={Icon.Moon}
				list={getOptions(store.item?.[PackagesFilterKeys.DURATION])}
				loading={store.loading}

				value={filters[PackagesFilterKeys.DURATION]}
				onChange={(values: Array<number>) => {
					if (!isArray(values)) return;

					handleSearch({
						[PackagesFilterKeys.DURATION]: values,
						[PackagesFilterKeys.PAGE]: initialPage,
					});
				}}
			/>
			<PanelWrapper
				title="Cruise Line / Tour Operator"
				Icon={Icon.Helm}
				list={getOptions(store.item?.[PackagesFilterKeys.CRUISELINE])}
				loading={store.loading}

				value={filters[PackagesFilterKeys.CRUISELINE]}
				onChange={(values: Array<string>) => {
					if (!isArray(values)) return;

					handleSearch({
						[PackagesFilterKeys.CRUISELINE]: values,
						[PackagesFilterKeys.PAGE]: initialPage,
					});
				}}
			/>
			<PanelWrapper
				title="Cruise Ship"
				Icon={Icon.Ship}
				list={getOptions(store.item?.[PackagesFilterKeys.SHIP])}
				loading={store.loading}

				value={filters[PackagesFilterKeys.SHIP]}
				onChange={(values: Array<string>) => {
					if (!isArray(values)) return;

					handleSearch({
						[PackagesFilterKeys.SHIP]: values,
						[PackagesFilterKeys.PAGE]: initialPage,
					});
				}}
			/>
		</SearchPackagesPageContent>
	);
};
